import { cn } from "@/lib/utils";
import { Mix_Content_Category_Enum } from "graphql/generated";
import { AppCategory } from "models/AppCategory";
import { ComponentProps, useState } from "react";

import { ContentCard } from "./ContentCard";
import {
    ContentAuthorName,
    ContentBookmark,
    ContentCardLayout,
    ContentCategoryChip,
    ContentTags,
    ContentThumbnailImg,
    ContentTitle,
    ContentUpdateTime
} from "./ContentCardLayout";

const ThumnailBox = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
    return (
        <div
            className={cn(
                `relative mb-3 aspect-[1.067] h-full w-full overflow-hidden
                rounded-xl`,
                className
            )}
            {...props}
        />
    );
};

const ThumnailOverlay = ({
    className,
    ...props
}: React.HTMLAttributes<HTMLDivElement>) => {
    return (
        <div
            className={cn("absolute h-full w-full rounded-xl", className)}
            {...props}
        />
    );
};

export const VerticalFullCard = ({
    content,
    interaction,
    className,
    ...props
}: Omit<ComponentProps<typeof ContentCard>, "type" | "content"> & {
    content: Mix_ContentFragment;
    className?: string;
}) => {
    const { onClick, ...layoutProps } = props;
    const [showFallback, setShowFallback] = useState(!content.imgurl);
    const [isHovered, setIsHovered] = useState(false);
    const showBookmark =
        isHovered || interaction === "hover" || !!content.bookmarked;

    return (
        <ContentCardLayout
            {...layoutProps}
            className={cn(
                "w-full",
                "group",
                "[--padding:16px]",
                // Thumbnail overlay
                "[&_.thumbnail-overlay]:bg-gradient-to-b",
                "[&_.thumbnail-overlay]:from-black/10",
                "[&_.thumbnail-overlay]:from-[43.23%]",
                "[&_.thumbnail-overlay]:to-black",
                "[&_.thumbnail-overlay]:bg-cover",
                "[&_.thumbnail-overlay]:bg-center",
                "[&_.thumbnail-overlay]:bg-no-repeat",
                showFallback && "[&_.thumbnail-overlay]:hidden",
                "hover:[&_.thumbnail-overlay]:bg-gradient-to-t",
                "hover:[&_.thumbnail-overlay]:from-black/20",
                "hover:[&_.thumbnail-overlay]:to-black/20",
                "[&[data-interaction='hover']_.thumbnail-overlay]:bg-gradient-to-t",
                "[&[data-interaction='hover']_.thumbnail-overlay]:from-black/20",
                "[&[data-interaction='hover']_.thumbnail-overlay]:to-black/20",
                "[&[data-bookmarked='true']_.thumbnail-overlay]:bg-gradient-to-t",
                "[&[data-bookmarked='true']_.thumbnail-overlay]:from-black/20",
                "[&[data-bookmarked='true']_.thumbnail-overlay]:to-black/20",
                className
            )}
            style={
                {
                    "--padding": "16px"
                } as React.CSSProperties
            }
            data-interaction={interaction}
            data-bookmarked={!!content.bookmarked}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
        >
            <ThumnailBox>
                <ContentThumbnailImg
                    className="rounded-xl transition-transform duration-300
                        ease-out group-hover:scale-105"
                    src={content.imgurl ?? ""}
                    unoptimized
                    fill={true}
                    onFallbackChange={setShowFallback}
                />
                {!showFallback && (
                    <ThumnailOverlay
                        className={cn(
                            "thumbnail-overlay",
                            showFallback && "hidden"
                        )}
                        style={{
                            background: showFallback
                                ? "none"
                                : "linear-gradient(180deg, rgba(0, 0, 0, 0.1) 43.23%, #000 100%), 50% / cover no-repeat"
                        }}
                    />
                )}
                <div
                    className="absolute left-0 top-0 z-[1] flex w-full
                        justify-between p-[var(--padding)]"
                >
                    {content.category && (
                        <ContentCategoryChip category={content.category} />
                    )}
                    {showBookmark && (
                        <div
                            className="animate-button-slide-in-delay-1
                                opacity-0"
                        >
                            <ContentBookmark
                                variant="filled"
                                content={content}
                            />
                        </div>
                    )}
                </div>
                <div
                    className="absolute bottom-0 left-0 z-[1] flex flex-col
                        p-[var(--padding)]"
                >
                    <ContentTitle className="mb-1 max-w-[80%] text-white">
                        {content.title}
                    </ContentTitle>
                    {content.title.includes("POST FORUM") ? (
                        <div
                            className="mb-3 flex flex-col
                                [&_span]:text-grey-400"
                        >
                            <div>
                                <ContentAuthorName>
                                    {
                                        AppCategory.toContentInfo(content).find(
                                            (item) => item.title === "주최"
                                        )?.content
                                    }
                                </ContentAuthorName>
                                <span>· </span>
                            </div>
                            <ContentUpdateTime
                                updatetime={content.updatetime}
                                offlinetime={content.offlinetime}
                                isOffline={
                                    content.category ===
                                    Mix_Content_Category_Enum.Offline
                                }
                            />
                        </div>
                    ) : (
                        <div className="mb-3 break-keep [&_span]:text-grey-400">
                            <ContentAuthorName>
                                {content.authorname}
                            </ContentAuthorName>
                            <span>· </span>
                            <ContentUpdateTime
                                updatetime={content.updatetime}
                                offlinetime={content.offlinetime}
                                isOffline={
                                    content.category ===
                                    Mix_Content_Category_Enum.Offline
                                }
                            />
                        </div>
                    )}
                    <ContentTags
                        tags={content.mix_content_tags}
                        color="primary.main"
                    />
                </div>
            </ThumnailBox>
        </ContentCardLayout>
    );
};
